import { MUSCLE_GROUP_OPTIONS, MUSCLE_GROUP_LABELS, type MuscleGroup } from '@/features/workouts/api/muscleGroups';

type MuscleGroupOption = (typeof MUSCLE_GROUP_OPTIONS)[number];

interface MuscleGroupSelectProps {
  value?: string[];
  onChange: (value: MuscleGroup[]) => void;
}

export function MuscleGroupSelect({ value = [], onChange }: MuscleGroupSelectProps) {
  const selected = value.filter((group): group is MuscleGroup => group in MUSCLE_GROUP_LABELS);
  const selectedSet = new Set(selected);

  const toggleGroup = (group: MuscleGroup) => {
    onChange(
      selectedSet.has(group)
        ? selected.filter((selectedGroup) => selectedGroup !== group)
        : [...selected, group]
    );
  };

  const clearSelection = () => onChange([]);

  const categories = MUSCLE_GROUP_OPTIONS.reduce<Record<string, MuscleGroupOption[]>>(
    (groups, option) => {
      groups[option.category] = [...(groups[option.category] || []), option];
      return groups;
    },
    {}
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 min-h-9" data-testid="exercise-muscle-groups-selected">
        {selected.length > 0 ? (
          selected.map((group) => (
            <button
              key={group}
              type="button"
              className="inline-flex items-center gap-1 rounded-lg border border-primary-500/30 bg-primary-50 px-3 py-1.5 text-sm font-medium text-primary-700 transition hover:bg-primary-100 dark:border-primary-400/30 dark:bg-primary-500/15 dark:text-primary-200"
              onClick={() => toggleGroup(group)}
              aria-label={`Remove ${MUSCLE_GROUP_LABELS[group]}`}
            >
              {MUSCLE_GROUP_LABELS[group]}
              <span aria-hidden="true">x</span>
            </button>
          ))
        ) : (
          <p className="text-sm text-gray-500 dark:text-slate-400">Choose one or more target areas.</p>
        )}
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-3 dark:border-white/10 dark:bg-slate-900/50">
        <div className="space-y-4">
          {Object.entries(categories).map(([category, options]) => (
            <fieldset key={category}>
              <legend className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
                {category}
              </legend>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {options.map((option) => {
                  const checked = selectedSet.has(option.value);

                  return (
                    <label
                      key={option.value}
                      className={`flex min-h-11 cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm transition ${
                        checked
                          ? 'border-primary-500 bg-primary-50 text-primary-700 dark:border-primary-400 dark:bg-primary-500/15 dark:text-primary-200'
                          : 'border-gray-200 bg-gray-50 text-gray-700 hover:border-gray-300 dark:border-white/10 dark:bg-white/5 dark:text-slate-300 dark:hover:border-white/20'
                      }`}
                    >
                      <input
                        data-testid={`exercise-muscle-group-${option.value}`}
                        type="checkbox"
                        className="rounded border-gray-300 text-primary-500 focus:ring-primary-500/50 dark:border-white/10"
                        checked={checked}
                        onChange={() => toggleGroup(option.value)}
                      />
                      <span>{option.label}</span>
                    </label>
                  );
                })}
              </div>
            </fieldset>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 text-xs text-gray-500 dark:text-slate-400">
        <span>{selected.length} selected</span>
        {selected.length > 0 && (
          <button
            data-testid="exercise-muscle-groups-clear"
            type="button"
            className="font-medium text-primary-600 hover:text-primary-700 dark:text-primary-300 dark:hover:text-primary-200"
            onClick={clearSelection}
          >
            Clear all
          </button>
        )}
      </div>
    </div>
  );
}
